import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControlLabel,
  Switch,
  Box,
  Alert,
} from '@mui/material';
import { useTranslation } from '../../i18n';
import { learningGroupService, CreateGroupData } from '../../services/learningGroupService';

interface CreateGroupDialogProps {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
  courseId?: number;
}

const CreateGroupDialog: React.FC<CreateGroupDialogProps> = ({
  open,
  onClose,
  onCreated,
  courseId,
}) => {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isPublic, setIsPublic] = useState(true);
  const [maxMembers, setMaxMembers] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      setError(t('learningGroups.errors.nameRequired', 'Name ist erforderlich'));
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const data: CreateGroupData = {
        name: name.trim(),
        description: description.trim() || undefined,
        isPublic,
        courseId,
        maxMembers: maxMembers ? parseInt(maxMembers) : undefined,
      };

      await learningGroupService.createGroup(data);
      handleClose();
      onCreated();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error creating group');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setName('');
    setDescription('');
    setIsPublic(true);
    setMaxMembers('');
    setError(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit}>
        <DialogTitle>{t('learningGroups.createDialog.title', 'Neue Lerngruppe erstellen')}</DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <TextField
            autoFocus
            fullWidth
            label={t('learningGroups.form.name', 'Gruppenname')}
            value={name}
            onChange={(e) => setName(e.target.value)}
            margin="normal"
            required
          />

          <TextField
            fullWidth
            label={t('learningGroups.form.description', 'Beschreibung')}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            margin="normal"
            multiline
            rows={3}
          />

          <TextField
            fullWidth
            label={t('learningGroups.form.maxMembers', 'Max. Mitglieder (optional)')}
            value={maxMembers}
            onChange={(e) => setMaxMembers(e.target.value.replace(/\D/g, ''))}
            margin="normal"
            type="number"
            inputProps={{ min: 2 }}
            helperText={t('learningGroups.form.maxMembersHelp', 'Leer lassen für unbegrenzt')}
          />

          <Box sx={{ mt: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={isPublic}
                  onChange={(e) => setIsPublic(e.target.checked)}
                />
              }
              label={t('learningGroups.form.isPublic', 'Öffentliche Gruppe')}
            />
            <Box sx={{ ml: 4, mt: 0.5 }}>
              <span style={{ fontSize: '0.75rem', color: '#666' }}>
                {isPublic
                  ? t('learningGroups.form.publicHelp', 'Jeder kann dieser Gruppe beitreten')
                  : t('learningGroups.form.privateHelp', 'Nur auf Einladung')}
              </span>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={loading}>
            {t('common.cancel', 'Abbrechen')}
          </Button>
          <Button type="submit" variant="contained" disabled={loading}>
            {loading
              ? t('common.saving', 'Speichern...')
              : t('learningGroups.createDialog.create', 'Erstellen')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default CreateGroupDialog;
